import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  FileText,
  FolderOpen,
  Monitor,
  Search,
  Shield,
  User,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { AlertData } from "../interfaces/CommonInterfaces";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import DownloadReportDialog from "./DownloadReportDialog";
import { Switch } from "../../ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { useEffect, useState } from "react";
import CloudEntityActivityLogs from "../CloudAnalytics/CloudEntityActivityLogs";
import {
  getAlertWhitelistedList,
  InfluxAlertWhitelisting,
  toggleAlertWhitelistStatus,
} from "./Api/alertsapi";
import { formatTimeAgo } from "../formatTimeAgo";
import ConfirmationDialog from "../Endpoints/ConfirmationDialog";
import { downloadAlertReportPDF } from "../downloadAlertReport";


type AlertRecentDataProps = {
  alerts: AlertData[];
  loading: boolean;
  tenantName: string;
  fromDetails?: boolean;
  computerId?: string;
  computeremail?: string;
  onWhitelistSelected?: (selectedAlerts: AlertData[]) => void | Promise<void>;
};

export default function AlertRecentData({
  alerts,
  loading,
  tenantName,
  computerId = "-1",
  computeremail = "",
  fromDetails = false,
  onWhitelistSelected,
}: AlertRecentDataProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedAlerts, setExpandedAlerts] = useState<Set<string>>(new Set());
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());
  const [selectedAlerts, setSelectedAlerts] = useState<Set<string>>(new Set());
  const [whitelistConfirmOpen, setWhitelistConfirmOpen] = useState(false);
  const [whitelistStatus, setWhitelistStatus] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: "",
    description: "",
  });
  const [isWhitelisting, setIsWhitelisting] = useState(false);
  const [search, setSearch] = useState("");
  const [viewWhitelistDialogOpen, setViewWhitelistDialogOpen] = useState(false);
  const [whitelistSearch, setWhitelistSearch] = useState("");
  const [whitelistCurrentPage, setWhitelistCurrentPage] = useState(1);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [whitelistRows, setWhitelistRows] = useState<InfluxAlertWhitelisting[]>([]);
  const [activeWhitelistSignatures, setActiveWhitelistSignatures] = useState<Set<string>>(new Set());
  const [togglingWhitelistKeys, setTogglingWhitelistKeys] = useState<Set<string>>(new Set());
  const [whitelistToggleConfirmOpen, setWhitelistToggleConfirmOpen] = useState(false);
  const [pendingWhitelistToggle, setPendingWhitelistToggle] = useState<{
    row: InfluxAlertWhitelisting;
    rowIndex: number;
    nextEnabled: boolean;
  } | null>(null);
  const itemsPerPage = 10;
  const whitelistItemsPerPage = 8;

  const getWhitelistProcessRaw = (row: InfluxAlertWhitelisting) =>
    String(row.process ?? row.Process ?? "");

  const getWhitelistProcess = (row: InfluxAlertWhitelisting) =>
    getWhitelistProcessRaw(row) || "-";

  const getWhitelistUser = (row: InfluxAlertWhitelisting) =>
    String(row.processUser ?? row.ProcessUser ?? row.user ?? "-");

  const getWhitelistProcessUser = (row: InfluxAlertWhitelisting) =>
    String(row.processUser ?? row.ProcessUser ?? row.user ?? "");

  const getWhitelistLevel = (row: InfluxAlertWhitelisting) =>
    String(row.level ?? row.Level ?? "Medium");

  const normalizeKeyValue = (value: unknown) =>
    String(value ?? "").trim().toLowerCase();

  const buildWhitelistSignature = (
    message: string,
    computerId: string,
    computerName: string,
    computerUserEmail: string,
    process: string
  ) =>
    [
      normalizeKeyValue(message),
      normalizeKeyValue(computerId),
      normalizeKeyValue(computerName),
      normalizeKeyValue(process),
      normalizeKeyValue(computerUserEmail),
    ].join("|");

  const getWhitelistSignatureFromRow = (row: InfluxAlertWhitelisting) =>
    buildWhitelistSignature(
      row.message || "",
      row.computerId || "",
      row.computerName || "",
      getWhitelistProcessRaw(row),
      getWhitelistProcessUser(row)
    );

  // Fix: Match signature fields and order with getWhitelistSignatureFromRow
  const getWhitelistSignatureFromAlert = (alert: AlertData) =>
    buildWhitelistSignature(
      alert.message || "",
      alert.computerId || "",
      alert.computerName || "",
      alert.processName || alert.Process || "",
      alert.computerUserEmail || alert.user || ""
    );

  const getWhitelistRowKey = (row: InfluxAlertWhitelisting, index: number) =>
    String(row.id ?? row.whitelistId ?? `${row.computerId}-${getWhitelistProcess(row)}-${row.message}-${index}`);

  const resolveWhitelistEnabled = (row: InfluxAlertWhitelisting) => {
    if (typeof row.enabled === "boolean") return row.enabled;
    if (typeof row.isEnabled === "boolean") return row.isEnabled;
    if (typeof row.isFlagged === "boolean") return !row.isFlagged;
    return true;
  };

  const refreshActiveWhitelistFilter = async () => {
    if (!tenantName) {
      setActiveWhitelistSignatures(new Set());
      return;
    }

    try {
      const response = await getAlertWhitelistedList(tenantName, "");
      const list = Array.isArray(response) ? response : [];
      const enabledWhitelistSignatures = new Set(
        list
          .filter((row) => resolveWhitelistEnabled(row))
          .map((row) => getWhitelistSignatureFromRow(row))
      );
      setActiveWhitelistSignatures(enabledWhitelistSignatures);
    } catch (error) {
      console.error("Unable to refresh whitelist filter:", error);
    }
  };

  const fetchWhitelistRows = async (searchParams: string) => {
    if (!tenantName) {
      setWhitelistStatus({
        open: true,
        title: "Tenant Missing",
        description: "Tenant is required to fetch whitelisted alerts.",
      });
      return;
    }

    try {
      setWhitelistLoading(true);
      const response = await getAlertWhitelistedList(tenantName, searchParams, computerId, computeremail);
      setWhitelistRows(Array.isArray(response) ? response : []);
      setWhitelistCurrentPage(1);
    } catch {
      setWhitelistStatus({
        open: true,
        title: "Fetch Failed",
        description: "Unable to fetch whitelisted alerts. Please try again.",
      });
    } finally {
      setWhitelistLoading(false);
    }
  };

  useEffect(() => {
    if (!viewWhitelistDialogOpen) {
      return;
    }

    const timer = setTimeout(() => {
      void fetchWhitelistRows(whitelistSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [viewWhitelistDialogOpen, whitelistSearch, tenantName]);

  useEffect(() => {
    void refreshActiveWhitelistFilter();
  }, [tenantName]);

  useEffect(() => {
    const nonWhitelistedIds = new Set(
      alerts
        .filter((alert) => !activeWhitelistSignatures.has(getWhitelistSignatureFromAlert(alert)))
        .map((alert) => alert.id)
    );

    setSelectedAlerts((prev) => {
      const next = new Set([...prev].filter((id) => nonWhitelistedIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [alerts, activeWhitelistSignatures]);

  const toggleWhitelistedRowState = async (
    row: InfluxAlertWhitelisting,
    index: number,
    enabled: boolean
  ) => {
    const rowKey = getWhitelistRowKey(row, index);

    if (!tenantName) {
      setWhitelistStatus({
        open: true,
        title: "Tenant Missing",
        description: "Tenant is required to update whitelist status.",
      });
      return;
    }

    setTogglingWhitelistKeys((prev) => {
      const next = new Set(prev);
      next.add(rowKey);
      return next;
    });

    try {
      const processValue = getWhitelistProcessRaw(row);
      const processUserValue = getWhitelistProcessUser(row);
      const levelValue = getWhitelistLevel(row);

      await toggleAlertWhitelistStatus(tenantName, {
        id: row.id,
        whitelistId: row.whitelistId,
        message: row.message || "",
        computerId: row.computerId || "",
        computerName: row.computerName || "",
        process: processValue,
        processUser: processUserValue,
        level: levelValue,
        enabled,
      });

      setWhitelistRows((prev) =>
        prev.map((item, rowIndex) => {
          const itemKey = getWhitelistRowKey(item, rowIndex);
          if (itemKey !== rowKey) return item;
          return {
            ...item,
            enabled,
            isEnabled: enabled,
            isFlagged: !enabled,
          };
        })
      );
      void refreshActiveWhitelistFilter();
    } catch {
      setWhitelistStatus({
        open: true,
        title: "Update Failed",
        description: "Unable to update whitelist state. Please try again.",
      });
    } finally {
      setTogglingWhitelistKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
    }
  };

  const requestWhitelistToggleConfirmation = (
    row: InfluxAlertWhitelisting,
    rowIndex: number,
    nextEnabled: boolean
  ) => {
    setPendingWhitelistToggle({ row, rowIndex, nextEnabled });
    setWhitelistToggleConfirmOpen(true);
  };

  const confirmWhitelistToggle = async () => {
    if (!pendingWhitelistToggle) {
      setWhitelistToggleConfirmOpen(false);
      return;
    }

    const { row, rowIndex, nextEnabled } = pendingWhitelistToggle;
    setWhitelistToggleConfirmOpen(false);
    setPendingWhitelistToggle(null);
    await toggleWhitelistedRowState(row, rowIndex, nextEnabled);
  };

  // Helper to check if alert or its eventJson matches search
  const alertMatchesSearch = (alert: AlertData, search: string) => {
    if (!search) return true;
    const lower = search.toLowerCase();
    // Check main fields
    for (const key of ["level", "computerName", "user", "processName", "message", "updatedAt"]) {
      if ((alert as any)[key]?.toString().toLowerCase().includes(lower)) return true;
    }
    // Check tags
    if ((alert as any).tags && Array.isArray((alert as any).tags)) {
      if ((alert as any).tags.some((tag: string) => tag.toLowerCase().includes(lower))) return true;
    }
    // Check eventJson
    try {
      const json = alert.eventjson ? JSON.stringify(JSON.parse(alert.eventjson)).toLowerCase() : "";
      if (json.includes(lower)) return true;
    } catch { }
    return false;
  };

  // Hide enabled whitelisted alerts and then apply search.
  const nonWhitelistedAlerts = alerts.filter(
    (alert) => !activeWhitelistSignatures.has(getWhitelistSignatureFromAlert(alert))
  );
  const filteredAlerts = nonWhitelistedAlerts.filter((a) => alertMatchesSearch(a, search));
  const filteredTotalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
  const filteredStartIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFilteredAlerts = filteredAlerts.slice(filteredStartIndex, filteredStartIndex + itemsPerPage);
  const whitelistTotalPages = Math.ceil(whitelistRows.length / whitelistItemsPerPage);
  const whitelistStartIndex = (whitelistCurrentPage - 1) * whitelistItemsPerPage;
  const paginatedWhitelistRows = whitelistRows.slice(
    whitelistStartIndex,
    whitelistStartIndex + whitelistItemsPerPage
  );
  const currentPageAlertIds = paginatedFilteredAlerts.map((alert) => alert.id);
  const isCurrentPageFullySelected =
    currentPageAlertIds.length > 0 && currentPageAlertIds.every((id) => selectedAlerts.has(id));
  const isCurrentPagePartiallySelected =
    currentPageAlertIds.some((id) => selectedAlerts.has(id)) && !isCurrentPageFullySelected;

  useEffect(() => {
    const maxPage = Math.max(filteredTotalPages, 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, filteredTotalPages]);

  const toggleAlertSelection = (alertId: string, checked: boolean | "indeterminate") => {
    setSelectedAlerts((prev) => {
      const next = new Set(prev);
      if (checked === true) {
        next.add(alertId);
      } else {
        next.delete(alertId);
      }
      return next;
    });
  };

  const toggleSelectAllCurrentPage = (checked: boolean | "indeterminate") => {
    setSelectedAlerts((prev) => {
      const next = new Set(prev);
      currentPageAlertIds.forEach((alertId) => {
        if (checked === true) {
          next.add(alertId);
        } else {
          next.delete(alertId);
        }
      });
      return next;
    });
  };

  const openWhitelistConfirmation = () => {
    if (selectedAlerts.size === 0 || isWhitelisting) {
      return;
    }
    setWhitelistConfirmOpen(true);
  };

  const handleWhitelistSelected = async () => {
    const selectedAlertRows = nonWhitelistedAlerts.filter((alert) => selectedAlerts.has(alert.id));
    setWhitelistConfirmOpen(false);

    if (selectedAlertRows.length === 0) {
      return;
    }

    if (!onWhitelistSelected) {
      setWhitelistStatus({
        open: true,
        title: "Whitelist Unavailable",
        description: "Whitelist action is not configured yet.",
      });
      return;
    }

    try {
      setIsWhitelisting(true);
      await Promise.resolve(onWhitelistSelected(selectedAlertRows));
      await refreshActiveWhitelistFilter();

      const selectedCount = selectedAlertRows.length;
      setSelectedAlerts(new Set());
      setWhitelistStatus({
        open: true,
        title: "Whitelist Submitted",
        description: `${selectedCount} alert${selectedCount > 1 ? "s" : ""} has been whitelisted.`,
      });
    } catch {
      setWhitelistStatus({
        open: true,
        title: "Whitelist Failed",
        description: "Unable to whitelist selected alerts. Please try again.",
      });
    } finally {
      setIsWhitelisting(false);
    }
  };

  const getAlertDetails = (alert: AlertData) => {

    let obj = {
      computerUserEmail: alert.user,
      computerName: alert.computerName,
      eventJson: alert.eventjson,
      message: alert.message,
      resourceName: alert.processName,
      sigma_results: alert.sigmaResults,
      time: alert.time,
    }

    return obj;
  };

  const getSeverityBadge = (level: string) => {
    switch (level.toLowerCase()) {
      case "critical":
        return (
          <Badge variant="destructive" className="text-xs">
            Critical
          </Badge>
        );
      case "high":
        return (
          <Badge className="text-xs text-white bg-orange-500 hover:bg-orange-600">
            High
          </Badge>
        );
      case "medium":
        return (
          <Badge className="text-xs text-white bg-yellow-500 hover:bg-yellow-600">
            Medium
          </Badge>
        );
      case "low":
        return (
          <Badge className="text-xs text-white bg-blue-500 hover:bg-blue-600">
            Low
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="text-xs">
            {level}
          </Badge>
        );
    }
  };

  const toggleAlertExpansion = (alertId: string) => {
    const newExpanded = new Set(expandedAlerts);
    if (newExpanded.has(alertId)) {
      newExpanded.delete(alertId);
    } else {
      newExpanded.add(alertId);
    }
    setExpandedAlerts(newExpanded);
  };

  const toggleTagsExpansion = (alertId: string) => {
    const newExpanded = new Set(expandedTags);
    if (newExpanded.has(alertId)) {
      newExpanded.delete(alertId);
    } else {
      newExpanded.add(alertId);
    }
    setExpandedTags(newExpanded);
  };

  // Smart pagination
  const getPageNumbers = (currentPage: number, totalPages: number) => {
    const delta = 2;
    const range: (number | string)[] = [];
    const rangeWithDots: (number | string)[] = [];
    let last: number | null = null;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (last) {
        if ((i as number) - last === 2) {
          rangeWithDots.push(last + 1);
        } else if ((i as number) - last !== 1) {
          rangeWithDots.push("…");
        }
      }
      rangeWithDots.push(i);
      last = i as number;
    }

    return rangeWithDots;
  };

  // Skeleton loader for rows
  const renderSkeletonRow = (key: number) => (
    <div
      key={key}
      className="grid gap-4 px-3 py-3 text-sm animate-pulse"
      style={{ gridTemplateColumns: "40px 100px 2fr 2fr 1fr" }}
    >
      <div className="w-4 h-4 bg-gray-300 rounded-sm" />
      <div className="w-20 h-6 bg-gray-300 rounded" />
      <div className="w-full h-6 bg-gray-300 rounded" />
      <div className="w-full h-6 bg-gray-300 rounded" />
      <div className="w-16 h-6 bg-gray-300 rounded" />
    </div>
  );

  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [includeAlertJson, setIncludeAlertJson] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      console.log(alerts)
      const logs = alerts.map(alert => ({
        tenantName: tenantName,
        _time: alert.time || "",
        computerUserEmail: alert.user || "",
        computerName: alert.computerName || "",
        resourceType: alert.level || "",
        resourceName: alert.processName || "",
        message: alert.message || "",
        alertJson: includeAlertJson ? alert.eventjson : undefined,
      }));
      const today = new Date().toISOString();
      await downloadAlertReportPDF(logs, [today, today], search, includeAlertJson);
      setDownloadDialogOpen(false);
      setIncludeAlertJson(false);
    } catch (e) {
      // Optionally show error toast
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {/* Search Filter */}

      <Card>
        <CardHeader>
          <CardTitle>
            <div className="flex items-center justify-between w-full gap-4">
              <span>Recent Alerts</span>
              <div className="flex items-center justify-end flex-1 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => setViewWhitelistDialogOpen(true)}
                  data-testid="button-view-whitelisted-alerts"
                >
                  View Whitelisted Alerts
                </Button>

                <div className="relative w-full max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Search className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    className="border rounded pl-9 pr-3 py-2 text-sm w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600"
                    placeholder="Search alerts"
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {selectedAlerts.size > 0 && (
                  <Button
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={openWhitelistConfirmation}
                    disabled={isWhitelisting}
                    data-testid="button-whitelist-selected"
                  >
                    {isWhitelisting ? "Whitelisting..." : `Whitelist (${selectedAlerts.size})`}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => setDownloadDialogOpen(true)}
                  data-testid="button-download-report"
                >
                  Download Report
                </Button>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Table Header */}
            <div
              className="grid gap-4 pb-2 text-xs font-medium tracking-wide uppercase border-b text-muted-foreground"
              style={{ gridTemplateColumns: "40px 100px 2fr 2fr 1fr" }}
            >
              <div className="flex justify-center">
                <Checkbox
                  checked={
                    isCurrentPageFullySelected
                      ? true
                      : isCurrentPagePartiallySelected
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={toggleSelectAllCurrentPage}
                  aria-label="Select all alerts on current page"
                  data-testid="checkbox-select-all-alerts"
                />
              </div>
              <div>Level</div>
              <div>Details</div>
              <div>Message</div>
              <div>Tags</div>
            </div>

            {/* Loading Skeleton */}
            {loading && (
              <div className="space-y-1">
                {[...Array(itemsPerPage)].map((_, idx) => renderSkeletonRow(idx))}
              </div>
            )}

            {/* No Data Found */}
            {!loading && filteredAlerts.length === 0 && (
              <div className="py-8 text-sm text-center text-muted-foreground">
                No alerts found.
              </div>
            )}

            {/* Table Rows */}
            {!loading && filteredAlerts.length > 0 && (
              <div className="space-y-1">
                {paginatedFilteredAlerts.map((alert) => {
                  const isExpanded = expandedAlerts.has(alert.id);
                  const details = getAlertDetails(alert);

                  return (
                    <div
                      key={alert.id}
                      className="border rounded-md border-border/50 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                      data-testid={`alert-${alert.id}`}
                    >
                      <div
                        className="grid gap-4 px-3 py-3 text-sm cursor-pointer hover-elevate"
                        style={{
                          gridTemplateColumns: "40px 100px 2fr 2fr 1fr",
                        }}
                        onClick={() => toggleAlertExpansion(alert.id)}
                      >
                        <div
                          className="flex items-center justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selectedAlerts.has(alert.id)}
                            onCheckedChange={(checked) => toggleAlertSelection(alert.id, checked)}
                            aria-label={`Select alert ${alert.id}`}
                            data-testid={`checkbox-alert-${alert.id}`}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                          {getSeverityBadge(alert.level)}
                        </div>

                        {/* Details Column - Computer/User/Process/Time */}
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-1 text-xs text-foreground">
                            <Monitor className="flex-shrink-0 w-3 h-3 text-muted-foreground" />
                            <span className="truncate">{alert.computerName}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="flex-shrink-0 w-3 h-3 text-muted-foreground" />
                            <span className="truncate">{alert.user}</span>
                          </div>
                          <div className="flex items-start gap-1 text-xs text-muted-foreground">
                            <FolderOpen className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <span className="font-mono text-xs leading-tight break-all">
                              {alert.processName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="flex-shrink-0 w-3 h-3 text-muted-foreground" />
                            <span>{alert.updatedAt}</span>
                          </div>
                        </div>

                        <div className="text-xs text-foreground">{alert.message}</div>

                        {/* Tags Column */}
                        <div className="space-y-1">
                          <div className="flex flex-wrap gap-1">
                            {(alert as any).tags?.slice(0, 2).map(
                              (tag: string, idx: number) => (
                                <Badge
                                  key={idx}
                                  className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-0.5"
                                >
                                  {tag}
                                </Badge>
                              )
                            )}
                            {(alert as any).tags?.length > 2 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 px-2 text-xs text-blue-600 hover:text-blue-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTagsExpansion(alert.id);
                                }}
                              >
                                {expandedTags.has(alert.id)
                                  ? "Show less"
                                  : `+${(alert as any).tags.length - 2} more`}
                              </Button>
                            )}
                          </div>
                          {expandedTags.has(alert.id) &&
                            (alert as any).tags?.length > 2 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(alert as any).tags
                                  .slice(2)
                                  .map((tag: string, idx: number) => (
                                    <Badge
                                      key={idx + 2}
                                      className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-0.5"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 rounded-lg border border-gray-200 dark:border-gray-700">
                          <h4 className="mb-3 text-sm font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
                            <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            Alert JSON
                          </h4>
                          {(() => {
                            let parsed = {};
                            try {
                              parsed = details?.eventJson ? JSON.parse(details.eventJson) : {};
                            } catch (e) {
                              parsed = {};
                            }

                            const isEmpty =
                              !parsed ||
                              (typeof parsed === "object" && Object.keys(parsed).length === 0);

                            return isEmpty ? (
                              <span className="text-sm text-muted-foreground">No data available.</span>
                            ) : (
                              <CloudEntityActivityLogs data={parsed} />
                            );
                          })()}

                        </div>

                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredAlerts.length > 0 && (
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  {getPageNumbers(currentPage, filteredTotalPages).map((page, idx) =>
                    typeof page === "number" ? (
                      <Button
                        key={idx}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className="h-8 min-w-8"
                      >
                        {page}
                      </Button>
                    ) : (
                      <span
                        key={idx}
                        className="px-2 select-none text-muted-foreground"
                      >
                        …
                      </span>
                    )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(filteredTotalPages, prev + 1))
                    }
                    disabled={currentPage === filteredTotalPages}
                    className="p-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={whitelistConfirmOpen} onOpenChange={setWhitelistConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Whitelist</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to whitelist {selectedAlerts.size} selected alert{selectedAlerts.size > 1 ? "s" : ""}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-whitelist">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleWhitelistSelected}
              data-testid="button-confirm-whitelist"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={whitelistStatus.open}
        onOpenChange={(open) => setWhitelistStatus((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{whitelistStatus.title}</AlertDialogTitle>
            <AlertDialogDescription>{whitelistStatus.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="button-whitelist-status-ok">OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={whitelistToggleConfirmOpen}
        onOpenChange={(open) => {
          setWhitelistToggleConfirmOpen(open);
          if (!open) {
            setPendingWhitelistToggle(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Whitelist Status</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingWhitelistToggle?.nextEnabled
                ? "Enable this whitelisted alert entry?"
                : "Disable this whitelisted alert entry?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-whitelist-toggle">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmWhitelistToggle}
              data-testid="button-confirm-whitelist-toggle"
            >
              {pendingWhitelistToggle?.nextEnabled ? "Enable" : "Disable"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={viewWhitelistDialogOpen} onOpenChange={setViewWhitelistDialogOpen}>
        <DialogContent
          className="max-w-5xl max-h-[85vh] overflow-hidden"
          style={{ fontFamily: "inherit" }}
        >
          <DialogHeader className="pb-1">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Whitelist Management
            </DialogTitle>
            <DialogDescription className="text-xs">
              Review each alert context and control whitelist state from one place.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-auto">
            <div className="flex items-center gap-2">
              <div className="relative w-full max-w-md">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Search className="w-4 h-4" />
                </span>
                <input
                  type="text"
                  className="border rounded pl-9 pr-3 py-2 text-xs w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600"
                  placeholder="Search whitelist"
                  value={whitelistSearch}
                  onChange={(e) => setWhitelistSearch(e.target.value)}
                  data-testid="input-search-whitelisted-alerts"
                />
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchWhitelistRows(whitelistSearch)}
                disabled={whitelistLoading}
                data-testid="button-refresh-whitelisted-alerts"
              >
                Refresh
              </Button>
            </div>

            {whitelistLoading && (
              <div className="py-8 text-xs text-center text-muted-foreground">
                Loading whitelisted alerts...
              </div>
            )}

            {!whitelistLoading && whitelistRows.length === 0 && (
              <div className="py-8 text-xs text-center text-muted-foreground">
                No whitelisted alerts found.
              </div>
            )}

            {!whitelistLoading && whitelistRows.length > 0 && (
              <>
                <div className="border rounded-md border-border/50">
                  <div
                    className="grid gap-4 px-4 py-3 text-xs font-medium tracking-wide uppercase border-b bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 text-muted-foreground"
                    style={{ gridTemplateColumns: "2.2fr 2.4fr 1.8fr 1.2fr" }}
                  >
                    <div className="flex items-center gap-2">
                      Alert
                    </div>
                    <div className="flex items-center gap-2">
                      Details
                    </div>
                    <div className="flex items-center gap-2">
                      Whitelisted
                    </div>
                    <div className="flex items-center gap-2">
                      Status
                    </div>
                  </div>

                  <div className="divide-y">
                    {paginatedWhitelistRows.map((row, index) => {
                      const rowKey = getWhitelistRowKey(row, whitelistStartIndex + index);
                      const enabled = resolveWhitelistEnabled(row);
                      const isUpdating = togglingWhitelistKeys.has(rowKey);
                      const userValue = getWhitelistUser(row);
                      const processValue = getWhitelistProcess(row);
                      const levelValue = getWhitelistLevel(row);
                      const createdAtValue = String(row.createdAt ?? "-");
                      const createdByValue = String(row.createdBy ?? userValue);

                      return (
                        <div
                          key={rowKey}
                          className="grid items-start gap-4 px-4 py-3 text-xs transition-colors hover:bg-muted/20"
                          style={{ gridTemplateColumns: "2.2fr 2.4fr 1.8fr 1.2fr" }}
                        >
                          <div className="min-w-0 space-y-2">
                            <div className="text-xs break-words whitespace-normal leading-relaxed" title={row.message}>
                              {row.message || "-"}
                            </div>
                            <div>{getSeverityBadge(levelValue)}</div>
                          </div>

                          <div className="min-w-0 space-y-1.5">
                            <div className="flex items-start gap-1.5 text-xs" title={row.computerName}>
                              <Monitor className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span className="break-words whitespace-normal leading-relaxed">
                                {row.computerName || "-"}
                              </span>
                            </div>
                            <div className="flex items-start gap-1.5 text-xs" title={userValue}>
                              <User className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span className="break-all whitespace-normal leading-relaxed">
                                {userValue || "-"}
                              </span>
                            </div>
                            <div className="flex items-start gap-1.5 text-xs" title={processValue}>
                              <FolderOpen className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span className="break-all whitespace-normal leading-relaxed">
                                {processValue || "-"}
                              </span>
                            </div>
                          </div>

                          <div className="min-w-0 space-y-1.5">
                            <div className="flex items-start gap-1.5 text-xs" title={createdByValue}>
                              <User className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span className="break-all whitespace-normal leading-relaxed">
                                {createdByValue || "-"}
                              </span>
                            </div>
                            <div className="flex items-start gap-1.5 text-xs" title={createdAtValue}>
                              <Clock className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span className="break-words whitespace-normal leading-relaxed">
                                {formatTimeAgo(createdAtValue) || "-"}
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-col items-start gap-2">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={enabled}
                                onCheckedChange={(checked) =>
                                  requestWhitelistToggleConfirmation(
                                    row,
                                    whitelistStartIndex + index,
                                    checked
                                  )
                                }
                                disabled={isUpdating}
                              />
                              <span className="text-xs text-muted-foreground">
                                {enabled ? "Whitelisted" : "Disabled"}
                              </span>
                            </div>

                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {whitelistTotalPages > 1 && (
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setWhitelistCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={whitelistCurrentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>

                    {getPageNumbers(whitelistCurrentPage, whitelistTotalPages).map((page, idx) =>
                      typeof page === "number" ? (
                        <Button
                          key={idx}
                          variant={whitelistCurrentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setWhitelistCurrentPage(page)}
                          className="h-8 min-w-8"
                        >
                          {page}
                        </Button>
                      ) : (
                        <span key={idx} className="px-2 select-none text-muted-foreground">
                          ...
                        </span>
                      )
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setWhitelistCurrentPage((prev) =>
                          Math.min(whitelistTotalPages, prev + 1)
                        )
                      }
                      disabled={whitelistCurrentPage === whitelistTotalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <DownloadReportDialog
        open={downloadDialogOpen}
        onOpenChange={setDownloadDialogOpen}
        onConfirm={handleDownloadReport}
        onCancel={() => setDownloadDialogOpen(false)}
        loading={isDownloading}
        includeAlertJson={includeAlertJson}
        setIncludeAlertJson={setIncludeAlertJson}
        title="Do you want to download the Alert logs report?"
        subtitle="Include alert JSON in PDF"
      />
    </>
  );
}


